import { useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";

export const useContentDelete = () => {
  const [deleting, setDeleting] = useState(false);
  const { toast } = useToast();

  const deleteContent = async (contentId: string) => {
    setDeleting(true);

    try {
      // Get content details first
      const { data: content, error: fetchError } = await supabase
        .from("content")
        .select("storage_path, thumbnail_url, user_id, type")
        .eq("id", contentId)
        .single();

      if (fetchError) throw fetchError;

      // Delete from storage if exists
      if (content.storage_path) {
        const { error: storageError } = await supabase.storage
          .from("content-uploads")
          .remove([content.storage_path]);

        if (storageError) {
          console.error("Storage deletion error:", storageError);
        }
      }

      // Delete thumbnail if exists (skip for audio/video/recording which use icons)
      const iconOnlyTypes = ["audio", "video", "recording"];
      if (content.thumbnail_url && !iconOnlyTypes.includes(content.type)) {
        const thumbnailPath = `${content.user_id}/${contentId}.png`;
        const { error: thumbError } = await supabase.storage
          .from("content-thumbnails")
          .remove([thumbnailPath]);

        if (thumbError) {
          console.error("Thumbnail deletion error:", thumbError);
        }
      }

      // Delete from database
      const { error: deleteError } = await supabase
        .from("content")
        .delete()
        .eq("id", contentId);

      if (deleteError) throw deleteError;

      toast({
        title: "Content deleted",
        description: "Your content has been deleted successfully.",
      });

      return true;
    } catch (error) {
      console.error("Delete error:", error);
      toast({
        title: "Delete failed",
        description: error.message || "Failed to delete content",
        variant: "destructive",
      });
      return false;
    } finally {
      setDeleting(false);
    }
  };

  return { deleteContent, deleting };
};
